/**
 * CancelAnnualModal — iter-230bw Phase 4
 * ---------------------------------------
 * Confirmation modal for annual cancellations. Pulls the live refund
 * estimate (we don't have a /preview endpoint yet so we surface the
 * stored billing_amount + a client-side ratio), then on confirm calls
 * POST /api/subscription-billing/cancel-annual.
 */
import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';

const formatINR = (v) =>
  v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function CancelAnnualModal({ isOpen, onClose, subscription, onCancelled }) {
  const { apiCall } = useAuth();
  const [confirming, setConfirming] = useState(false);

  const refundEstimate = useMemo(() => {
    if (!subscription?.billing_amount) return { rupees: null, daysRemaining: null };
    const end = subscription?.current_period_end
      ? new Date(subscription.current_period_end).getTime()
      : null;
    const start = subscription?.current_period_start
      ? new Date(subscription.current_period_start).getTime()
      : null;
    if (!end || !start) return { rupees: null, daysRemaining: null };
    const total = end - start;
    const remaining = Math.max(end - Date.now(), 0);
    const ratio = total > 0 ? remaining / total : 0;
    const days = Math.round(remaining / 86_400_000);
    const rupees = Math.round(Number(subscription.billing_amount) * ratio * 100) / 100;
    return { rupees, daysRemaining: days };
  }, [subscription]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await apiCall('/api/subscription-billing/cancel-annual', {
        method: 'POST',
        body: JSON.stringify({ confirm: true }),
      });
      toast.success(
        `Refund of ${formatINR(res.refund_total)} initiated · credit note ${res.credit_note_number}`
      );
      onCancelled?.(res);
      onClose?.();
    } catch (e) {
      toast.error(e?.message || 'Could not cancel annual subscription.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="cancel-annual-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Cancel annual subscription?
          </DialogTitle>
          <DialogDescription>
            Your storefront will stop taking orders and your data will go into Holiday Mode until you re-subscribe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="refund-estimate">
            <p className="font-semibold text-amber-900">Estimated refund</p>
            <p className="text-amber-800 mt-1">
              {refundEstimate.rupees != null
                ? <>~ <span className="font-bold">{formatINR(refundEstimate.rupees)}</span> back to your original payment method ({refundEstimate.daysRemaining} days remaining)</>
                : 'We\'ll calculate the pro-rated refund on confirmation.'}
            </p>
            <p className="text-xs text-amber-700 mt-2">
              A GST credit note will be issued and the refund typically lands in 5-7 working days.
            </p>
          </div>
          <p className="text-xs text-slate-500">
            Final refund amount is calculated by the backend at the moment of cancellation and may differ
            slightly from the estimate above.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={confirming}>Keep my subscription</Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={confirming}
            data-testid="cancel-annual-confirm"
          >
            {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Yes, cancel & refund
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
